import type { Action, Discard, GameView, Meld, RoomInfo, Tile, WinInfo } from "./types";
import { backHtml, kindOf, kindTileHtml, tileHtml } from "./tiles";
import { ABORTS, CALLS, LIMITS, WINDS, WIND_KANJI, YAKU } from "./i18n";

export interface Choice {
  action: Action;
  tiles: Tile[];
}

export const ui = {
  selected: null as Tile | null,
  riichiMode: false,
  chooser: null as Choice[] | null,
  lastEventSeq: -1,
  callout: null as null | { seat: number; kind: string; until: number },
  resultHidden: false,
};

export function resetUi() {
  ui.selected = null;
  ui.riichiMode = false;
  ui.chooser = null;
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}

const nameOf = (room: RoomInfo, seat: number) => room.seats[seat]?.name ?? `Игрок ${seat + 1}`;

// ---------- pieces ----------

function meldHtml(m: Meld, owner: number, cls = ""): string {
  const tiles = [...m.tiles];
  if (m.type === "ankan") {
    // Closed kan: outer tiles face down.
    return `<span class="meld ${cls}">${backHtml()}${tileHtml(tiles[1])}${tileHtml(tiles[2])}${backHtml()}</span>`;
  }
  // Position of the sideways (called) tile depends on who it came from.
  const rel = (m.from - owner + 4) % 4; // 1 = right (shimocha), 2 = across, 3 = left (kamicha)
  const others = tiles.filter((t) => t !== m.called);
  let added: Tile | null = null;
  if (m.type === "kakan") {
    added = others.pop()!;
  }
  const calledHtml = tileHtml(m.called, { sideways: true, cls: added !== null ? "stack-base" : "" });
  const calledBlock = added !== null
    ? `<span class="kakan">${tileHtml(added, { sideways: true })}${calledHtml}</span>`
    : calledHtml;
  const parts = others.map((t) => tileHtml(t));
  const idx = rel === 3 ? 0 : rel === 2 ? 1 : parts.length;
  parts.splice(idx, 0, calledBlock);
  return `<span class="meld ${cls}">${parts.join("")}</span>`;
}

function pondHtml(discards: Discard[], highlightLast: boolean): string {
  const rows: Discard[][] = [discards.slice(0, 6), discards.slice(6, 12), discards.slice(12)];
  return rows.map((row, ri) => `<div class="prow">${row.map((d, i) => {
    const last = highlightLast && ri * 6 + i === discards.length - 1;
    const cls = [d.called ? "called" : "", d.tsumogiri ? "tsumogiri" : "", last ? "last" : ""].join(" ");
    return tileHtml(d.tile, { sideways: d.riichi, cls });
  }).join("")}</div>`).join("");
}

function roundName(g: GameView): string {
  return `${WINDS[g.roundWind] ?? "Запад"} ${g.roundNum + 1}`;
}

// ---------- table ----------

export function renderGame(g: GameView, room: RoomInfo, deadline: number): string {
  const me = g.seat >= 0 ? g.seat : 0;
  const pos = (s: number) => (s - me + 4) % 4;
  let html = `<div class="game">`;
  html += `<div class="board">`;

  // Center
  html += `<div class="center">`;
  html += `<div class="cinfo"><div class="rname">${roundName(g)}</div>
    <div class="sub">${g.honba ? `${g.honba} хонба` : ""}${g.honba && g.sticks ? " · " : ""}${g.sticks ? `${g.sticks} × 1000` : ""}</div>
    <div class="wall">Стена: <b>${g.wallLeft}</b></div>
    <div class="dora">${g.dora.map((t) => tileHtml(t)).join("")}${Array.from({ length: 5 - g.dora.length }, () => backHtml()).join("")}</div></div>`;
  for (let s = 0; s < 4; s++) {
    const p = g.players[s];
    const active = (g.phase === "turn" && g.turn === s) ? "active" : "";
    const off = room.seats[s] && !room.seats[s]!.online ? `<span class="offline" title="не в сети">⚠</span>` : "";
    html += `<div class="slabel p${pos(s)} ${active} ${s === g.dealer ? "dealer" : ""}">
      <span class="wind">${WIND_KANJI[p.wind]}</span>
      <span class="sname">${esc(nameOf(room, s))}${off}</span>
      <span class="score">${p.score}</span>
      ${p.riichi ? `<span class="stick"></span>` : ""}
    </div>`;
  }
  html += `</div>`;

  // Zones
  for (let s = 0; s < 4; s++) {
    const p = g.players[s];
    const isLastDiscarder = g.lastDiscarder === s && (g.phase === "claim" || g.phase === "turn") && g.turn !== s;
    html += `<div class="zone p${pos(s)}">`;
    html += `<div class="pond">${pondHtml(p.discards, isLastDiscarder)}</div>`;
    if (pos(s) !== 0 || g.seat < 0) {
      const n = p.handCount - (p.hasDrawn ? 1 : 0);
      html += `<div class="ohand">${Array.from({ length: n }, () => backHtml()).join("")}${p.hasDrawn ? `<span class="gap"></span>${backHtml()}` : ""}
        <span class="melds">${p.melds.slice().reverse().map((m) => meldHtml(m, s)).join("")}</span></div>`;
    }
    html += `</div>`;
  }

  // Callout
  if (ui.callout && Date.now() < ui.callout.until) {
    const c = ui.callout;
    const where = c.seat >= 0 ? `p${pos(c.seat)}` : "mid";
    html += `<div class="callout ${where} k-${c.kind}">${CALLS[c.kind] ?? c.kind}</div>`;
  }
  html += `</div>`; // board

  // My area
  if (g.seat >= 0) html += renderMine(g, deadline);
  else html += `<div class="mine spectator">Вы наблюдаете за игрой</div>`;

  // Modals
  if (g.phase === "result" && g.result && !ui.resultHidden) html += renderResult(g, room);
  if (g.phase === "end" && g.final) html += renderFinal(g, room);
  html += `</div>`;
  return html;
}

function renderMine(g: GameView, deadline: number): string {
  const o = g.options;
  const p = g.players[g.seat];
  const allowed = new Set<Tile>((ui.riichiMode ? o?.riichi : o?.discard) || []);
  const canDiscard = g.phase === "turn" && !!o?.discard;
  const tileCls = (t: Tile) => {
    const c = ["htile"];
    if (canDiscard) c.push(allowed.has(t) ? "can" : "dim");
    if (ui.selected === t) c.push("sel");
    return c.join(" ");
  };
  const hand = (g.hand || []).map((t) => tileHtml(t, { cls: tileCls(t), attrs: `data-act="tile" data-tile="${t}"` })).join("");
  const drawn = g.drawn !== undefined
    ? `<span class="gap"></span>${tileHtml(g.drawn, { cls: tileCls(g.drawn) + " drawn", attrs: `data-act="tile" data-tile="${g.drawn}"` })}`
    : "";
  const melds = p.melds.slice().reverse().map((m) => meldHtml(m, g.seat)).join("");

  // Buttons
  const btns: string[] = [];
  const b = (act: string, label: string, cls = "") => btns.push(`<button class="btn act ${cls}" data-act="${act}">${label}</button>`);
  if (o && g.phase !== "result") {
    if (o.ron) b("ron", "Рон", "win");
    if (o.tsumo) b("tsumo", "Цумо", "win");
    if (o.riichi?.length) b("riichi", ui.riichiMode ? "Отмена риичи" : "Риичи", ui.riichiMode ? "on" : "riichi");
    if (o.pon?.length) b("pon", "Пон");
    if (o.chi?.length) b("chi", "Чи");
    if (o.kan || o.ankan?.length || o.kakan?.length) b("kan", "Кан");
    if (o.kyuushu) b("kyuushu", "9 терминалов");
    if (o.pass) b("pass", "Пропустить", "pass");
  }
  if (g.phase === "result" && ui.resultHidden) b("hideResult", "Показать итоги");
  if (touchMode() && document.fullscreenEnabled && !document.fullscreenElement) {
    btns.push(`<button class="btn tiny fs" data-act="fullscreen" title="На весь экран">⛶ На весь экран</button>`);
  }

  let chooser = "";
  if (ui.chooser) {
    chooser = `<div class="chooser">${ui.chooser.map((c, i) => {
      const label = c.action.type === "kan" ? `Кан ${tileHtml(lastDiscardTile(g) ?? 0)}` :
        c.action.type === "kakan" || c.action.type === "ankan" ? `Кан ${tileHtml(c.tiles[0])}` :
        c.tiles.map((t) => tileHtml(t)).join("");
      return `<button class="btn choice" data-act="choose" data-idx="${i}">${label}</button>`;
    }).join("")}<button class="btn pass" data-act="cancel">✕</button></div>`;
  }

  let status = "";
  if (ui.riichiMode) status = "Выберите тайл для сброса с риичи";
  else if (g.phase === "turn" && g.turn === g.seat && canDiscard) status = touchMode() ? "Ваш ход — нажмите тайл дважды" : "Ваш ход";
  else if (g.phase === "claim" && o) status = "Заявка на сброс?";

  let waits = "";
  if (g.waits?.length) {
    waits = `<div class="waits">Ждёте: ${g.waits.map((k) => kindTileHtml(k, "mini")).join("")}${g.furiten ? `<span class="furiten">Фуритэн</span>` : ""}</div>`;
  }

  return `<div class="mine">
    <div class="bar">
      <div class="status">${status}${waits}</div>
      <div class="buttons">${btns.join("")}</div>
      ${g.phase === "result" ? "" : `<div id="timer" class="timer" ${deadline ? "" : "hidden"}></div>`}
    </div>
    ${chooser}
    <div class="handrow">
      <div class="hand ${ui.riichiMode ? "riichimode" : ""}">${hand}${drawn}</div>
      <div class="melds mymelds">${melds}</div>
    </div>
  </div>`;
}

function touchMode(): boolean {
  return window.matchMedia("(hover: none)").matches;
}

function lastDiscardTile(g: GameView): Tile | null {
  const p = g.players[g.lastDiscarder];
  const d = p?.discards[p.discards.length - 1];
  return d ? d.tile : null;
}

// ---------- results ----------

function scoreTable(g: GameView, room: RoomInfo, deltas: number[], scores: number[]): string {
  const order = [0, 1, 2, 3].sort((a, b) => scores[b] - scores[a] || a - b);
  return `<table class="scores">${order.map((s) => {
    const d = deltas[s];
    const dcls = d > 0 ? "plus" : d < 0 ? "minus" : "";
    return `<tr class="${s === g.seat ? "me" : ""}"><td class="kj">${WIND_KANJI[g.players[s].wind]}</td><td>${esc(nameOf(room, s))}</td>
      <td class="num">${scores[s]}</td><td class="num ${dcls}">${d > 0 ? "+" : ""}${d || ""}</td></tr>`;
  }).join("")}</table>`;
}

function winHtml(w: WinInfo, g: GameView, room: RoomInfo): string {
  const r = w.result;
  const tsumo = w.from === w.seat;
  const title = tsumo ? `${esc(nameOf(room, w.seat))} — цумо` : `${esc(nameOf(room, w.seat))} — рон с ${esc(nameOf(room, w.from))}`;
  const yaku = r.yaku.map((y) => `<tr><td>${YAKU[y.key] ?? y.key}</td><td class="num">${y.yakuman ? (y.yakuman > 1 ? `${y.yakuman}× якуман` : "якуман") : `${y.han} хан`}</td></tr>`);
  if (!r.yakuman) {
    if (r.dora) yaku.push(`<tr><td>Дора</td><td class="num">${r.dora} хан</td></tr>`);
    if (r.aka) yaku.push(`<tr><td>Ака-дора</td><td class="num">${r.aka} хан</td></tr>`);
    if (r.ura) yaku.push(`<tr><td>Ура-дора</td><td class="num">${r.ura} хан</td></tr>`);
  }
  const value = r.yakuman ? (LIMITS.yakuman + (r.yakuman > 1 ? ` ×${r.yakuman}` : "")) :
    `${r.han} хан ${r.fu} фу${r.limit ? ` — ${LIMITS[r.limit]}` : ""}`;
  const hand = w.hand.map((t) => tileHtml(t)).join("");
  const melds = w.melds.map((m) => meldHtml(m, w.seat)).join("");
  return `<div class="win">
    <h3>${title}</h3>
    <div class="rhand">${hand}<span class="gap"></span>${tileHtml(w.tile, { cls: "wintile" })}<span class="gap"></span>${melds}</div>
    <div class="indicators"><span>Дора:</span>${g.result!.dora.map((t) => tileHtml(t, { cls: "mini" })).join("")}
      ${w.ura?.length ? `<span>Ура:</span>${w.ura.map((t) => tileHtml(t, { cls: "mini" })).join("")}` : ""}</div>
    <table class="yaku">${yaku.join("")}</table>
    <div class="value">${value} <b>${w.points}</b></div>
  </div>`;
}

function renderResult(g: GameView, room: RoomInfo): string {
  const res = g.result!;
  let body = "";
  if (res.type === "ron" || res.type === "tsumo") {
    body = res.wins!.map((w) => winHtml(w, g, room)).join("");
  } else if (res.type === "abort") {
    body = `<h3>Абортивная ничья</h3><p>${ABORTS[res.reason || ""] ?? res.reason}</p>`;
  } else {
    body = `<h3>${res.type === "nagashi" ? "Нагаси манган" : "Ничья — стена закончилась"}</h3>`;
    const tenpai = res.tenpai || [];
    body += tenpai.length
      ? tenpai.map((s) => `<div class="tenpai"><div>${esc(nameOf(room, s))} — темпай</div><div class="rhand">${(res.hands[s] || []).map((t) => tileHtml(t)).join("")}
          <span class="gap"></span>${g.players[s].melds.map((m) => meldHtml(m, s)).join("")}</div></div>`).join("")
      : `<p>Все нотэн</p>`;
  }
  const o = g.options;
  const btn = o?.next
    ? `<button class="btn primary" data-act="next">${res.gameOver ? "К итогам игры" : "Далее"}</button>`
    : `<span class="muted">Ждём остальных игроков…</span>`;
  return `<div class="modal"><div class="dialog">
    <div class="dhead"><span>${roundName(g)}${g.honba ? `, ${g.honba} хонба` : ""}</span><button class="btn tiny" data-act="hideResult" title="Посмотреть на стол">👁</button></div>
    ${body}
    ${scoreTable(g, room, res.deltas, res.scores)}
    <div class="dfoot">${btn}<span id="timer" class="timer"></span></div>
  </div></div>`;
}

function renderFinal(g: GameView, room: RoomInfo): string {
  const rows = g.final!.map((st) => `<tr class="${st.seat === g.seat ? "me" : ""}"><td class="place">${st.place}</td><td>${esc(nameOf(room, st.seat))}</td><td class="num">${st.score}</td>
    <td class="num ${st.score >= 25000 ? "plus" : "minus"}">${((st.score - 25000) / 1000).toFixed(1)}</td></tr>`).join("");
  const again = room.isHost ? `<button class="btn primary" data-act="lobby">Новая игра</button>` : `<span class="muted">Хост может начать новую игру</span>`;
  return `<div class="modal"><div class="dialog final">
    <h2>Игра окончена</h2>
    <table class="scores">${rows}</table>
    <div class="dfoot">${again}</div>
  </div></div>`;
}

export { kindOf };
